import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, AlertTriangle, TrendingUp, Wallet, Plus, ArrowRight, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import DynamicCustomFields from '@/components/DynamicCustomFields';

const RELATIONSHIPS = [
  { id: 'wholly_owned', label: 'Wholly Owned' },
  { id: 'majority', label: 'Majority (>50%)' },
  { id: 'minority', label: 'Minority (<50%)' },
  { id: 'jv', label: 'Joint Venture' },
  { id: 'sponsored', label: 'Sponsored Company' },
  { id: 'investee', label: 'Investee' },
  { id: 'partner', label: 'Partner' },
  { id: 'other', label: 'Other' },
];

const STATUSES = ['active', 'dormant', 'dissolved', 'pending'];

function Kpi({ label, value, hint, icon: Icon, accent = false }) {
  return (
    <div className={`ve-card p-5 ${accent ? 'border-[#8B3FC5] bg-gradient-to-br from-[#FFF8E5] to-white' : ''}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">{label}</span>
        <span className="h-8 w-8 rounded-md bg-[#8B3FC5]/15 text-[#5E1F8B] flex items-center justify-center">
          <Icon size={14} strokeWidth={1.6}/>
        </span>
      </div>
      <div className="display text-3xl mt-3 font-bold">{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-1.5">{hint}</div>}
    </div>
  );
}

function fmt(n) {
  if (n == null) return '—';
  const v = Number(n);
  if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v) >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return v.toFixed(0);
}

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch + submit effects. */
export default function EntitiesDashboard() {
  const [dash, setDash] = useState(null);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const initial = {
    legal_name: '', trade_name: '', legal_type: 'LLC', jurisdiction: 'Qatar',
    registered_address: '', cr_number: '', cr_expiry_date: '',
    baladiya_card_number: '', baladiya_expiry_date: '',
    relationship_type: 'wholly_owned', our_equity_percentage: 0,
    sector: '', primary_contact_name: '', primary_contact_email: '',
    total_invested: 0, outstanding_liabilities: 0, status: 'active',
  };
  const [form, setForm] = useState(initial);
  // Tenant-defined custom fields (Form Designer → 'entity' form_key)
  // are collected separately and merged into the payload as custom_fields.
  const [customFields, setCustomFields] = useState({});

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get('/v2/entities/dashboard'),
        api.get('/v2/entities/', { params: search ? { q: search } : {} }),
      ]);
      setDash(a.data);
      setItems(b.data);
    } catch { toast.error('Could not load entity dashboard'); }
  };
  useEffect(() => { load(); }, [search]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.legal_name) return toast.error('Legal name required');
    try {
      const payload = { ...form };
      ['cr_expiry_date', 'baladiya_expiry_date'].forEach((k) => {
        if (payload[k]) payload[k] = new Date(payload[k]).toISOString();
        else delete payload[k];
      });
      ['our_equity_percentage', 'total_invested', 'outstanding_liabilities'].forEach((k) => {
        payload[k] = parseFloat(payload[k]) || 0;
      });
      if (customFields && Object.keys(customFields).length) {
        payload.custom_fields = customFields;
      }
      await api.post('/v2/entities/', payload);
      toast.success('Entity created.');
      setOpen(false);
      setForm(initial);
      setCustomFields({});
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create entity');
    }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="ve-eyebrow">Ruʾya · رؤية · Holding Portfolio</div>
          <h1 className="display text-3xl md:text-4xl mt-2 font-bold">Ruʾya — your bird&apos;s-eye view.</h1>
          <p className="text-sm text-slate-600 dark:text-white/60 mt-2 max-w-2xl">A single source of truth for CR / Baladiya / Tax cards, financials, investments and liabilities — across every entity in the holding.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="v2-entity-create" className="ve-btn-primary self-start"><Plus size={14}/> New entity</button>
      </div>

      {/* KPIs */}
      {dash && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi label="Entities" value={dash.total_entities} hint="Active + all statuses" icon={Building2}/>
          <Kpi label="Total Invested" value={`${fmt(dash.total_invested)}`} hint="Across portfolio (QAR)" icon={TrendingUp} accent/>
          <Kpi label="Liabilities" value={`${fmt(dash.total_liabilities)}`} hint="Outstanding (QAR)" icon={Wallet}/>
          <Kpi label="Dividends YTD" value={`${fmt(dash.dividends_ytd)}`} hint="Year-to-date (QAR)" icon={TrendingUp}/>
        </div>
      )}

      {/* Expiry alerts */}
      {dash && (dash.expired?.length || dash.expiring_soon?.length) ? (
        <div className="mt-8 ve-card p-6 border-amber-300/60 bg-amber-50/40">
          <div className="flex items-center gap-2 text-amber-900">
            <AlertTriangle size={16}/>
            <h2 className="display text-lg font-bold">Renewals requiring action</h2>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            {dash.expired.map((e, i) => (
              <Link key={i} to={`/dashboard/entities-os/${e.entity_id}`} className="flex items-center justify-between p-3 rounded-md bg-rose-50 border border-rose-200 hover:bg-rose-100 transition-colors">
                <div>
                  <div className="text-sm font-medium">{e.legal_name}</div>
                  <div className="text-xs text-rose-700 mt-0.5">{e.kind} EXPIRED {Math.abs(e.days_left)} days ago</div>
                </div>
                <ArrowRight size={14} className="text-rose-700"/>
              </Link>
            ))}
            {dash.expiring_soon.map((e, i) => (
              <Link key={i} to={`/dashboard/entities-os/${e.entity_id}`} className="flex items-center justify-between p-3 rounded-md bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors">
                <div>
                  <div className="text-sm font-medium">{e.legal_name}</div>
                  <div className="text-xs text-amber-800 mt-0.5">{e.kind} expires in {e.days_left} days</div>
                </div>
                <ArrowRight size={14} className="text-amber-700"/>
              </Link>
            ))}
          </div>
        </div>
      ) : null}

      {/* Search bar */}
      <div className="mt-8 flex items-center gap-3 max-w-md">
        <Search size={14} className="text-slate-400"/>
        <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="v2-entity-search" placeholder="Search legal name, trade name, CR number…" className="flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20"/>
      </div>

      {/* Table */}
      <div className="mt-4 ve-card overflow-x-auto">
        {items.length === 0 ? (
          <div className="p-10 text-center">
            <Building2 size={28} className="mx-auto text-slate-300 mb-3"/>
            <h3 className="display text-xl font-bold">No entities yet</h3>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.15em] text-[10px] font-bold">
              <tr>
                <th className="text-left px-5 py-3">Legal Name</th>
                <th className="text-left px-5 py-3">Type</th>
                <th className="text-left px-5 py-3">Jurisdiction</th>
                <th className="text-left px-5 py-3">Relationship</th>
                <th className="text-right px-5 py-3">Our %</th>
                <th className="text-right px-5 py-3">Invested</th>
                <th className="text-left px-5 py-3">CR Expiry</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-right px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((e) => (
                <tr key={e.entity_id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-3">
                    <Link to={`/dashboard/entities-os/${e.entity_id}`} className="font-medium hover:text-[#5E1F8B]">{e.legal_name}</Link>
                    {e.trade_name && <div className="text-xs text-slate-500 mt-0.5">{e.trade_name}</div>}
                  </td>
                  <td className="px-5 py-3">{e.legal_type}</td>
                  <td className="px-5 py-3">{e.jurisdiction}</td>
                  <td className="px-5 py-3 capitalize">{(e.relationship_type || '').replace(/_/g, ' ')}</td>
                  <td className="px-5 py-3 text-right mono">{e.our_equity_percentage?.toFixed(1) ?? '—'}%</td>
                  <td className="px-5 py-3 text-right mono">{fmt(e.total_invested)}</td>
                  <td className="px-5 py-3 mono text-xs">{e.cr_expiry_date ? new Date(e.cr_expiry_date).toLocaleDateString() : '—'}</td>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded font-bold ${
                      e.status === 'active' ? 'bg-emerald-50 text-emerald-700' :
                      e.status === 'dormant' ? 'bg-slate-100 text-slate-600' :
                      e.status === 'dissolved' ? 'bg-rose-50 text-rose-700' :
                      'bg-amber-50 text-amber-700'
                    }`}>{e.status}</span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <Link to={`/dashboard/entities-os/${e.entity_id}`} className="ve-btn-ghost text-xs">Open <ArrowRight size={12}/></Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Composition charts */}
      {dash && dash.total_entities > 0 && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { title: 'By Status', data: dash.by_status },
            { title: 'By Relationship', data: dash.by_relationship },
            { title: 'By Sector', data: dash.by_sector },
          ].map((c) => (
            <div key={c.title} className="ve-card p-5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">{c.title}</div>
              <div className="mt-4 space-y-2">
                {Object.entries(c.data).map(([k, v]) => {
                  const pct = (v / dash.total_entities) * 100;
                  return (
                    <div key={k}>
                      <div className="flex justify-between text-xs">
                        <span className="capitalize">{k.replace(/_/g, ' ')}</span>
                        <span className="mono text-slate-500">{v}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-slate-100 mt-1">
                        <div className="h-full rounded-full bg-gradient-to-r from-[#8B3FC5] to-[#5E1F8B]" style={{width: `${pct}%`}}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
          <form onSubmit={submit} className="ve-card p-7 w-full max-w-3xl bg-white max-h-[88vh] overflow-y-auto">
            <div className="flex items-center justify-between sticky top-0 bg-white pb-4 border-b border-slate-200">
              <h2 className="display text-2xl font-bold">New entity</h2>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={18}/></button>
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { f: 'legal_name', l: 'Legal Name *', span: 2 },
                { f: 'trade_name', l: 'Trade Name', span: 2 },
                { f: 'legal_type', l: 'Legal Type (LLC, SPC, JV…)' },
                { f: 'jurisdiction', l: 'Jurisdiction' },
                { f: 'sector', l: 'Sector' },
                { f: 'relationship_type', l: 'Relationship', select: RELATIONSHIPS },
                { f: 'our_equity_percentage', l: 'Our Equity %', type: 'number' },
                { f: 'status', l: 'Status', select: STATUSES.map((s) => ({ id: s, label: s })) },
                { f: 'cr_number', l: 'CR Number' },
                { f: 'cr_expiry_date', l: 'CR Expiry', type: 'date' },
                { f: 'baladiya_card_number', l: 'Baladiya Card #' },
                { f: 'baladiya_expiry_date', l: 'Baladiya Expiry', type: 'date' },
                { f: 'total_invested', l: 'Total Invested (QAR)', type: 'number' },
                { f: 'outstanding_liabilities', l: 'Liabilities (QAR)', type: 'number' },
                { f: 'primary_contact_name', l: 'Contact Name' },
                { f: 'primary_contact_email', l: 'Contact Email', type: 'email' },
                { f: 'registered_address', l: 'Registered Address', span: 2 },
              ].map((c) => (
                <label key={c.f} className={`text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold ${c.span === 2 ? 'md:col-span-2' : ''}`}>
                  {c.l}
                  {c.select ? (
                    <select data-testid={`v2-entity-${c.f}`} value={form[c.f]} onChange={(e) => setForm({...form, [c.f]: e.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal font-normal">
                      {c.select.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                  ) : (
                    <input data-testid={`v2-entity-${c.f}`} type={c.type || 'text'} value={form[c.f]} onChange={(e) => setForm({...form, [c.f]: e.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal font-normal"/>
                  )}
                </label>
              ))}
            </div>
            <DynamicCustomFields
              formKey="entity"
              values={customFields}
              onChange={setCustomFields}
              className="mt-6 pt-5 border-t border-slate-200"
            />
            <div className="mt-7 flex justify-end gap-3 pt-4 border-t border-slate-200 sticky bottom-0 bg-white">
              <button type="button" onClick={() => setOpen(false)} className="ve-btn-secondary">Cancel</button>
              <button type="submit" data-testid="v2-entity-submit" className="ve-btn-primary">Create entity</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
